/**
 * Text and score formatting utilities
 */

import { getBrandName } from '@shared/config/brand';

/**
 * Convert snake_case or kebab-case strings to Title Case.
 * e.g. "billing_dispute" → "Billing Dispute"
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * A source or connector type for display. The platform's own type, "xygo", shows as the
 * configured brand (brandName in public/config.js); any other type is returned unchanged.
 */
export const typeLabel = (type) => (type === 'xygo' ? getBrandName() : type);

/**
 * Sentiment score (-1…+1) → 0-100%.
 * -1 → 0%, 0 → 50%, +1 → 100%
 */
export const sentimentToPercent = (score) => {
  const n = Number(score ?? 0);
  if (isNaN(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(((n + 1) / 2) * 100)));
};

/**
 * Sentiment score (-1…+1) → 0-100% with 2 decimal places.
 */
export const sentimentToPercentDecimal = (score) => {
  const n = Number(score ?? 0);
  if (isNaN(n)) return '0.00';
  return (Math.min(100, Math.max(0, ((n + 1) / 2) * 100))).toFixed(2);
};

/**
 * QA score (0…1) → 0-100%.
 * 0 → 0%, 0.66 → 66%, 1 → 100%
 */
export const qaToPercent = (score) => {
  const n = Number(score ?? 0);
  if (isNaN(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n * 100)));
};

/**
 * ISO date string → short display. "2026-03-15" → "Mar 15"
 */
export const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/**
 * The agent to show for a connector interaction: the name its source gave,
 * else the identifier its conversations are grouped by.
 */
export const agentLabel = (interaction) =>
  interaction?.assigned_to || interaction?.agent_identifier || '—';
